import React, { useState, useMemo } from 'react';

const Sidebar = ({ activeMenu, onMenuChange, isCollapsed, onToggleCollapse, pagesPermissions = [], user = null }) => {
  // Static menu items definition (for fallback or when permissions not loaded)
  const allMenuItems = [
    { icon: 'fa-robot', label: 'PDF Parser Smart', id: 'smart-parser', slug: 'smart-parser' },
    { icon: 'fa-file-invoice-dollar', label: 'Laporan Keuangan Analysis', id: 'financial-analysis', slug: 'financial-analysis' },
    { icon: 'fa-magnifying-glass-chart', label: 'FinSight', id: 'finsight', slug: 'finsight' },
    { icon: 'fa-bolt', label: 'Smart FinSight', id: 'smart-finsight', slug: 'smart-finsight' },
    { icon: 'fa-clipboard-check', label: 'Audit Plan Analysis', id: 'audit-plan-analysis', slug: 'audit-plan-analysis' },
    { icon: 'fa-flask', label: 'RnD Prompting', id: 'rnd-prompting', slug: 'rnd-prompting' },
    { icon: 'fa-microchip', label: 'LLM Configuration', id: 'llm-config', slug: 'llm-config' },
    { icon: 'fa-chart-line', label: 'Analytics', id: 'analytics', slug: 'analytics' },
    { icon: 'fa-clock-rotate-left', label: 'History', id: 'history', slug: 'history' },
  ];

  const adminMenuItem = { icon: 'fa-shield-halved', label: 'Admin Area', id: 'admin-area', slug: 'admin-area' };

  // Filter menu items based on permissions
  const navigationItems = useMemo(() => {
    // If no permissions loaded yet, show empty array (don't show all items)
    // This ensures menu is only shown after permissions are loaded
    if (!pagesPermissions || pagesPermissions.length === 0) {
      return [];
    }

    // Create dynamic menu items from API pages with permissions
    const dynamicItems = pagesPermissions
      .filter(pageWithPerm => {
        // Filter out admin-area (handled separately) and ensure page is active
        if (!pageWithPerm.page || pageWithPerm.page.slug === 'admin-area') {
          return false;
        }
        // Only include pages with can_view permission
        return pageWithPerm.permissions && pageWithPerm.permissions.can_view === true && pageWithPerm.page.is_active;
      })
      .map(pageWithPerm => {
        // Find corresponding menu item config or create from page data
        const menuItem = allMenuItems.find(item => item.slug === pageWithPerm.page.slug);
        return {
          icon: menuItem ? menuItem.icon : (pageWithPerm.page.icon || 'fa-file'),
          label: pageWithPerm.page.name,
          id: pageWithPerm.page.slug,
          slug: pageWithPerm.page.slug,
          active: activeMenu === pageWithPerm.page.slug,
          sort_order: pageWithPerm.page.sort_order || 0
        };
      })
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

    // Return only items with permissions, don't fallback to all items
    return dynamicItems;
  }, [pagesPermissions, activeMenu]);

  // Check if user can view Admin Area
  const canViewAdmin = useMemo(() => {
    if (!user) return false;
    if (user.is_superuser) return true;

    if (!pagesPermissions || pagesPermissions.length === 0) {
      return false;
    }

    const adminPerm = pagesPermissions.find(p => p.page && p.page.slug === 'admin-area');
    return adminPerm && adminPerm.permissions && adminPerm.permissions.can_view === true;
  }, [pagesPermissions, user]);

  const adminItems = canViewAdmin ? [
    { ...adminMenuItem, active: activeMenu === 'admin-area' }
  ] : [];

  return (
    <aside className={`fixed left-0 top-0 h-screen bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex-shrink-0 hidden md:block z-50 transition-all duration-300 ${isCollapsed ? 'w-20' : 'w-64'
      }`}>
      <div className="flex flex-col h-full">
        {/* Logo */}
        <div className="h-16 border-b border-gray-200 dark:border-gray-700 flex items-center justify-center">
          {isCollapsed ? (
            <img
              src={`${process.env.PUBLIC_URL}/square_tabulara.png`}
              alt="Tabulara Logo"
              className="h-8 max-w-[2rem] object-contain dark:brightness-0 dark:invert transition-all duration-300"
            />
          ) : (
            <img
              src={`${process.env.PUBLIC_URL}/lg_tabulara.png`}
              alt="Tabulara Logo"
              className="h-8 object-contain dark:brightness-0 dark:invert transition-all duration-300"
            />
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto overflow-x-visible py-4">
          <ul className="space-y-1 px-3">
            {/* Dashboard Menu - Always Available */}
            <li className="relative group overflow-visible">
              <span
                onClick={() => onMenuChange && onMenuChange('dashboard')}
                className={`flex items-center py-2.5 text-sm font-medium rounded-lg cursor-pointer transition-colors ${isCollapsed ? 'px-3 justify-center' : 'px-3'
                  } ${activeMenu === 'dashboard'
                    ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
              >
                <i className={`fa-solid fa-home w-5 h-5 ${isCollapsed ? '' : 'mr-3'
                  } ${activeMenu === 'dashboard' ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400'
                  }`}></i>
                {!isCollapsed && (
                  <span className="flex-1">Dashboard</span>
                )}
              </span>
              {/* Tooltip saat collapsed */}
              {isCollapsed && (
                <div className="absolute left-full ml-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-[100] pointer-events-none shadow-lg">
                  Dashboard
                  <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900 dark:border-r-gray-700"></div>
                </div>
              )}
            </li>

            {/* Divider */}
            {navigationItems.length > 0 && (
              <li className="py-2">
                <div className="border-t border-gray-200 dark:border-gray-700"></div>
              </li>
            )}

            {navigationItems.map((item, index) => (
              <li key={index} className="relative group overflow-visible">
                <span
                  onClick={() => onMenuChange && onMenuChange(item.id)}
                  className={`flex items-center py-2.5 text-sm font-medium rounded-lg cursor-pointer transition-colors ${isCollapsed ? 'px-3 justify-center' : 'px-3'
                    } ${item.active
                      ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                >
                  <i className={`fa-solid ${item.icon} w-5 h-5 ${isCollapsed ? '' : 'mr-3'
                    } ${item.active ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400'
                    }`}></i>
                  {!isCollapsed && (
                    <span className="flex-1">{item.label}</span>
                  )}
                </span>
                {/* Tooltip saat collapsed */}
                {isCollapsed && (
                  <div className="absolute left-full ml-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-[100] pointer-events-none shadow-lg">
                    {item.label}
                    <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900 dark:border-r-gray-700"></div>
                  </div>
                )}
              </li>
            ))}
          </ul>

          {/* Admin Area Separator */}
          <div className="px-3 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <ul className="space-y-1">
              {adminItems.map((item, index) => (
                <li key={index} className="relative group overflow-visible">
                  <span
                    onClick={() => onMenuChange && onMenuChange(item.id)}
                    className={`flex items-center py-2.5 text-sm font-medium rounded-lg cursor-pointer transition-colors ${isCollapsed ? 'px-3 justify-center' : 'px-3'
                      } ${item.active
                        ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                  >
                    <i className={`fa-solid ${item.icon} w-5 h-5 ${isCollapsed ? '' : 'mr-3'
                      } ${item.active ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400'
                      }`}></i>
                    {!isCollapsed && (
                      <span className="flex-1">{item.label}</span>
                    )}
                  </span>
                  {/* Tooltip saat collapsed */}
                  {isCollapsed && (
                    <div className="absolute left-full ml-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-[100] pointer-events-none shadow-lg">
                      {item.label}
                      <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900 dark:border-r-gray-700"></div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </nav>

        {/* Collapse Button */}
        <div className="border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onToggleCollapse}
            className={`w-full flex items-center py-3 px-3 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${isCollapsed ? 'justify-center' : ''
              }`}
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? (
              <i className="fa-solid fa-angle-double-right text-sm"></i>
            ) : (
              <i className="fa-solid fa-angle-double-left text-sm"></i>
            )}
            {!isCollapsed && (
              <span className="ml-3 text-sm font-medium">Collapse</span>
            )}
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar; 